import React, { useState, useEffect } from 'react';
import { getMyReferralStats } from '../../api/referrals';
import Header from '../../components/Header/Header';
import Tooltip from '@mui/material/Tooltip';
import './ReferralDashboard.css';
import bonus1 from '../../assets/images/rewards/bonus-1.png';
import bonus2 from '../../assets/images/rewards/bonus-2.png';
import bonus3 from '../../assets/images/rewards/bonus-3.png';
import bonus4 from '../../assets/images/rewards/bonus-4.png';
import bonus5 from '../../assets/images/rewards/bonus-5.png';
import bonus6 from '../../assets/images/rewards/bonus-6.png';
import bonus7 from '../../assets/images/rewards/bonus-7.png';
import bonus8 from '../../assets/images/rewards/bonus-8.png';
import airpodsRight from '../../assets/images/gifs/airpod-right.gif';
import airpodsLeft from '../../assets/images/gifs/airpod-left.gif';
import Rellax from 'rellax';

const ReferralDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showCreditPopup, setShowCreditPopup] = useState(false);
  const [creditAmount, setCreditAmount] = useState(0);

  const bonusImages = [bonus1, bonus2, bonus3, bonus4, bonus5, bonus6, bonus7, bonus8];

  const bonusTooltips = {
    1: "On your 5th referral, you get a beautiful mowPod tee shirt! Let us know the size and we'll mail it out to you!",
    2: "Double Bonus!!",
    3: "Get a Creator Ticket to Podfest! We'll even throw in a bonus cocktail party ticket.",
    4: "Get a 3-night hotel stay at Podfest! (standard room at the event hotel)",
    5: "Triple Bonus!!!",
    6: "Get a Shure MV7 Microphone!",
    7: "Get a 1 year subscription to Streamyard!",
    8: "Quadruple Bonus!!!!"
  };

  useEffect(() => {
    loadStats();
    document.body.classList.add('referrals-page');
    return () => {
      document.body.classList.remove('referrals-page');
    };
  }, []);

  useEffect(() => {
    if (!loading) {
      window.scrollTo(0, 0);
      const timer = setTimeout(() => {
        const rellax = new Rellax('.rellax', {
          center: true,
        });

        return () => {
          rellax.destroy();
        };
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [loading]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await getMyReferralStats();

      // TEST Generate mock referrals if less than 50
      if (data.referrals && data.referrals.length < 50) {
        const currentCount = data.referrals.length;
        const mockReferrals = [];

        for (let i = currentCount; i < 50; i++) {
          mockReferrals.push({
            id: `mock-${i}`,
            created_at: null
          });
        }

        data.referrals = [...data.referrals, ...mockReferrals];
      }

      setStats(data);
    } catch (error) {
      console.error('Error loading referral stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(type);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareOnSocial = (platform) => {
    const url = stats.referral_link;
    const text = `Get $50 off your first podcast ad campaign! Use my referral link`;

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        url
      )}&quote=${encodeURIComponent(text)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(
        url
      )}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      email: `mailto:?subject=${encodeURIComponent('Check out MowPod!')}&body=${encodeURIComponent(
        text + '\n\n' + url
      )}`,
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      registered: '#ffa500',
      campaign_started: '#3498db',
      reward_pending: '#9b59b6',
      reward_paid: '#27ae60',
    };
    return colors[status] || '#95a5a6';
  };

  const getStatusLabel = (status) => {
    const labels = {
      registered: 'Registered',
      campaign_started: 'Campaign Started',
      reward_pending: 'Under Review',
      reward_paid: 'Reward Paid',
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="referral-dashboard loading">
        <div className="spinner"></div>
        <p>Loading your referral dashboard...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="referral-dashboard error">
        <p>Unable to load referral data. Please try again later.</p>
      </div>
    );
  }

  const progressPercent = stats.next_milestone
    ? ((stats.successful_referrals / stats.next_milestone.threshold) * 100).toFixed(1)
    : 100;

  return (
    <>
      <Header />
      <div className="background__rainbow">
        <div className="background__rainbow--image rainbow--first thin rellax" />
        <div className="background__rainbow--image rainbow--second referral-rainbow rellax" />
      </div>
      <img className="airpod-right rellax" data-rellax-speed={2} src={airpodsRight} alt="airpod-right" />
      <img className="airpod-left rellax" data-rellax-speed={2} src={airpodsLeft} alt="airpod-left" />
      <div className="referral-dashboard">
        <div className="referral-header">
          <h1>
            Referral <br /> Program
          </h1>
          <p className="subtitle">Share your code and earn cash towards your next campaign!</p>
        </div>

        {/* Stats Bar */}
        <div className="stats-bar">
          <span className="stats-item">
            Earned Credit: <strong>${(stats.earned_credit || 0).toFixed(2)}</strong>
          </span>
          <span className="stats-item">
            Credit Balance: <strong>${stats.credit_balance.toFixed(2)}</strong>
          </span>
          <button
            className="use-credit-btn"
            onClick={() => {
              setCreditAmount(Math.min(stats.credit_balance, 50));
              setShowCreditPopup(true);
            }}
            disabled={stats.credit_balance <= 0}
          ></button>
        </div>

        {/* Credit Popup */}
        {showCreditPopup && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '30px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              }}
            >
              <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#f59a02' }}>Use Credit</h2>
              <p style={{ marginBottom: '20px', color: '#666' }}>
                Select the amount of credit you want to use for your next campaign.
              </p>
              <div style={{ marginBottom: '30px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: '#333' }}>
                  Credit Amount: ${creditAmount.toFixed(2)}
                </label>
                <input
                  type="range"
                  min="0"
                  max={stats.credit_balance}
                  step="1"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(parseFloat(e.target.value))}
                  style={{
                    width: '100%',
                    height: '8px',
                    borderRadius: '4px',
                    outline: 'none',
                    background: `linear-gradient(to right, #667eea 0%, #667eea ${
                      (creditAmount / stats.credit_balance) * 100
                    }%, #ddd ${(creditAmount / stats.credit_balance) * 100}%, #ddd 100%)`,
                  }}
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '5px',
                    fontSize: '12px',
                    color: '#999',
                  }}
                >
                  <span>$0</span>
                  <span>${stats.credit_balance.toFixed(2)}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowCreditPopup(false)}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '6px',
                    border: '1px solid #333',
                    backgroundColor: 'white',
                    color: '#333',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    //uc=use credit, ac=amount credit
                    window.location.href = `/?uc=true&aс=${creditAmount}`;
                  }}
                  disabled={creditAmount <= 0}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: creditAmount > 0 ? '#5568d3' : '#999',
                    color: 'white',
                    cursor: creditAmount > 0 ? 'pointer' : 'not-allowed',
                    fontSize: '14px',
                    fontWeight: '600',
                  }}
                >
                  Apply Credit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Referral Code & Link */}
        <div className="referral-code-section">
          <h2>Your Referral Link</h2>
          <div className="code-container">
            <div className="link-display">
              <input type="text" value={stats.referral_link} readOnly className="link-input" />
              <button
                className="copy-button submit-boost--button submit-button"
                onClick={() => copyToClipboard(stats.referral_link, 'link')}
              >
                {copied === 'link' ? '✓ Copied!' : 'Copy Link'}
              </button>
            </div>
          </div>

          {/* Social Share Buttons */}
          <div className="share-buttons">
            <h3>Share on:</h3>
            <div className="social-buttons">
              <button onClick={() => shareOnSocial('facebook')} className="social-btn facebook">
                Facebook
              </button>

              <button onClick={() => shareOnSocial('linkedin')} className="social-btn linkedin">
                LinkedIn
              </button>
              <button onClick={() => shareOnSocial('twitter')} className="social-btn twitter">
                X
              </button>
              <button onClick={() => shareOnSocial('email')} className="social-btn email">
                Email
              </button>
            </div>
          </div>
        </div>

        {/* Progress Thermometer */}
        {stats.next_milestone && (
          <div className="milestone-section">
            <h2>Next Milestone</h2>
            <div className="thermometer-container">
              <div className="thermometer">
                <div className="thermometer-fill" style={{ height: `${progressPercent}%` }}></div>
                <div className="thermometer-value">{stats.successful_referrals}</div>
              </div>
              <div className="milestone-info">
                <h3>{stats.next_milestone.reward_name}</h3>
                <p className="milestone-progress">{stats.next_milestone.remaining} more referrals to go!</p>
                <p className="milestone-target">
                  Goal: {stats.next_milestone.threshold} successful referrals
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Milestones List */}
        {stats.milestones && stats.milestones.length > 0 && (
          <div className="milestones-list">
            <h2>All Milestones</h2>
            <div className="milestone-items">
              {stats.milestones.map((milestone, index) => (
                <div key={index} className={`milestone-item ${milestone.achieved ? 'achieved' : ''}`}>
                  <div className="milestone-icon">{milestone.achieved ? '✓' : milestone.threshold}</div>
                  <div className="milestone-details">
                    <h4>{milestone.reward_name}</h4>
                    {milestone.reward_description && <p>{milestone.reward_description}</p>}
                    <span className="milestone-threshold">{milestone.threshold} referrals</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Referral History */}
        <div className="referral-history">
          <h2>Referral History</h2>
          <div className="referral-table-wrapper">
            <table className="referral-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Reward</th>
                  <th>Bonus</th>
                </tr>
              </thead>
              <tbody>
                {stats.referrals && stats.referrals.length > 0 ? (
                  stats.referrals.map((referral, index) => {
                    const referralNumber = index + 1;
                    const isMilestone = referralNumber % 5 === 0;
                    const bonusImageNumber = isMilestone ? Math.floor(referralNumber / 5) : null;

                    // Check if current and next image exist
                    const currentImageExists = bonusImageNumber && bonusImages[bonusImageNumber - 1];
                    const nextMilestone = referralNumber + 5 - (referralNumber % 5);
                    const nextBonusNumber = Math.floor(nextMilestone / 5);
                    const nextImageExists = bonusImages[nextBonusNumber - 1];

                    // Only show image if both current exists and (it's the last one OR next one exists)
                    const shouldShowImage =
                      currentImageExists && (bonusImageNumber === bonusImages.length || nextImageExists);

                    return (
                      <tr key={referral.id}>
                        <td>{referralNumber}</td>
                        <td>{referral.created_at ? new Date(referral.created_at).toLocaleDateString() : '-'}</td>
                        <td className="reward-amount">
                          {referral.reward_amount ? `$${referral.reward_amount}` : '-'}
                        </td>
                        <td>
                          {shouldShowImage ? (
                          <Tooltip title={bonusTooltips[bonusImageNumber]} arrow placement="left">
                              <span style={{ display: 'inline-block', cursor: 'help' }}>
                                <img 
                                  src={bonusImages[bonusImageNumber - 1]} 
                                  alt={`Bonus ${bonusImageNumber}`}
                                />
                              </span>
                            </Tooltip>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td>0</td>
                    <td>-</td>
                    <td>$0</td>
                    <td>-</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* How It Works */}
        <div className="how-it-works">
          <h2>How It Works</h2>
          <div className="steps">
            <div className="step">
              <h3 className="step-number">1</h3>
              <div className="step-content">
                <h3>Share Your Code</h3>
                <p>Send your referral link to friends via social media, email, or messages.</p>
              </div>
            </div>
            <div className="step">
              <h3 className="step-number">2</h3>
              <div className="step-content">
                <h3>They Sign Up</h3>
                <p>Your friends gets $50 off their first campaign!</p>
              </div>
            </div>
            <div className="step">
              <h3 className="step-number">3</h3>
              <div className="step-content">
                <h3>You Earn Rewards</h3>
                <p>Once they complete their campaign, you receive $50 in credit!</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReferralDashboard;
